<template>
  <div :key="currentIndex">
    <div>
      <teleport to="body">
        <loading />
      </teleport>
      <header-bar v-if="routeLayout.isHeader" @refresh="currentIndex++" /><!-- 상단 헤더바 -->
      <router-view /><!-- 라우터 페이지 -->
      <footer-bar v-if="routeLayout.isFooter" />
    </div>
    <!-- 전체화면 modal component -->
    <teleport to="body" v-for="(item, index) in modalComponents" :key="index">
      <component
        :index="index"
        :is="item.component"
        :layout="item.layout"
        :data="item.data"
        :callBack="item.callBack"
        @close="fnCloseModal"
      />
    </teleport>
    <!-- alert modal component -->
    <teleport to="body" v-for="(item, index) in modalAlertComponents" :key="index">
      <component
        :index="index"
        :is="item.component"
        :layout="item.layout"
        :data="item.data"
        :callBack="item.callBack"
        @close="fnCloseModalAlert"
      />
    </teleport>
  </div>
</template>

<script>
import HeaderBar from '@/components/common/layouts/header-bar/index';
import FooterBar from '@/components/common/layouts/footer-bar/index';
import Loading from '@/components/common/layouts/loading/index';
import { computed, watch, reactive, toRefs, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  SESSION_ACTIVITY_EVENTS,
  SESSION_ACTIVITY_SYNC_INTERVAL_MS,
  SESSION_LAST_ACTIVE_AT_KEY
} from '@/common/constants/Session';
import { getRemainingSessionTime, isSessionExpired } from '@/common/utils/SessionTimeout';

const layoutState = () => {
  const { getters, dispatch } = useStore();
  const state = reactive({
    routeLayout: computed(() => getters['layout/getRouteLayout']),
    modalAlertComponents: computed(() => getters['layout/getModalAlertComponents']),
    modalComponents: computed(() => getters['layout/getModalComponents']),
    currentIndex: 0
  });

  /** methods **/
  const fnCloseModalAlert = (index = 0) => {
    dispatch('layout/closeModalAlertComponent', { index });
  };
  const fnCloseModal = (index = 0) => {
    dispatch('layout/closeModalComponent', { index });
  };
  return {
    ...toRefs(state),
    fnCloseModalAlert,
    fnCloseModal
  };
};

const routeState = () => {
  const route = useRoute();
  /** watch **/
  watch(route, () => {
    console.log('watch route', route.fullPath);
  });
};

const sessionState = () => {
  const store = useStore();
  const route = useRoute();
  const router = useRouter();

  let timeoutId = null;
  let isSessionExpiring = false;

  const isAuthenticated = computed(() => {
    return store.getters['expert/getIsAuthenticated'];
  });

  const clearSessionTimeout = () => {
    if (!timeoutId) return;
    window.clearTimeout(timeoutId);
    timeoutId = null;
  };

  const moveToLogin = async ({ expired = false } = {}) => {
    if (route.name === 'ExpertLogin') return;

    const query = expired ? { expired: '1' } : {};
    await router.replace({
      name: 'ExpertLogin',
      query
    });
  };

  const expireSession = async ({ expired = false } = {}) => {
    if (isSessionExpiring) return;

    isSessionExpiring = true;
    clearSessionTimeout();
    await store.dispatch('expert/expireSession');
    await moveToLogin({ expired });
    isSessionExpiring = false;
  };

  const syncSessionTimeout = async () => {
    clearSessionTimeout();

    if (!isAuthenticated.value) return;

    if (isSessionExpired()) {
      await expireSession({ expired: true });
      return;
    }

    timeoutId = window.setTimeout(() => {
      expireSession({ expired: true });
    }, getRemainingSessionTime());
  };

  const stopExpiredEvent = (event) => {
    if (!event) return;

    if (typeof event.preventDefault === 'function') {
      event.preventDefault();
    }

    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }

    if (typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
  };

  const touchSession = async (timestamp = Date.now()) => {
    const lastActiveAt = store.getters['expert/getLastActiveAt'];
    const remainingTime = getRemainingSessionTime(timestamp);

    if (
      lastActiveAt &&
      timestamp - lastActiveAt < SESSION_ACTIVITY_SYNC_INTERVAL_MS &&
      remainingTime > SESSION_ACTIVITY_SYNC_INTERVAL_MS
    ) {
      await syncSessionTimeout();
      return;
    }

    await store.dispatch('expert/touchSession', {
      lastActiveAt: timestamp
    });
    await syncSessionTimeout();
  };

  const handleUserActivity = async (event) => {
    if (!isAuthenticated.value) {
      clearSessionTimeout();
      return;
    }

    if (event && event.type === 'visibilitychange' && document.visibilityState !== 'visible') {
      return;
    }

    if (isSessionExpired()) {
      stopExpiredEvent(event);
      await expireSession({ expired: true });
      return;
    }

    await touchSession();
  };

  const handleStorage = async (event) => {
    if (event.key === 'accessToken' && event.newValue === null) {
      await expireSession();
      return;
    }

    if (event.key === SESSION_LAST_ACTIVE_AT_KEY || event.key === 'data') {
      await syncSessionTimeout();
    }
  };

  onMounted(() => {
    SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, handleUserActivity, true);
    });
    document.addEventListener('visibilitychange', handleUserActivity, true);
    window.addEventListener('storage', handleStorage);
  });

  onBeforeUnmount(() => {
    clearSessionTimeout();

    SESSION_ACTIVITY_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleUserActivity, true);
    });
    document.removeEventListener('visibilitychange', handleUserActivity, true);
    window.removeEventListener('storage', handleStorage);
  });

  watch(
    isAuthenticated,
    async (value) => {
      if (!value) {
        clearSessionTimeout();
        return;
      }

      await syncSessionTimeout();
    },
    { immediate: true }
  );

  watch(
    () => route.fullPath,
    async () => {
      if (!isAuthenticated.value) return;
      await touchSession();
    }
  );
};

export default {
  name: 'index',
  components: { HeaderBar, FooterBar, Loading },
  setup() {
    routeState();
    sessionState();
    return {
      ...layoutState()
    };
  }
};
</script>

<style scoped></style>
